import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

const RESULTS_ID = 'recently-viewed-results';

/**
 * Fills in the "Recently Viewed" product section from the client-side
 * RecentlyViewed store (localStorage).
 *
 * There's no native Shopify endpoint for a client-tracked list, so this
 * fetches sections/recently-viewed-results.liquid through the Section
 * Rendering API against /search?q=id:X OR id:Y — the same lookup
 * predictive-search.js uses for its own recently-viewed row, and the
 * reason the fetch target has to be a standalone section file (see the
 * comments in that section and in blocks/recently-viewed-products.liquid).
 *
 * @extends {Component}
 */
class RecentlyViewedProductsBlock extends Component {
  /** @type {IntersectionObserver} */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecentlyViewed();
    },
    { rootMargin: '0px 0px 1500px 0px' } // early enough that a fast scroll doesn't land on the swap
  );

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
  }

  async #loadRecentlyViewed() {
    const { sectionId, searchUrl, currentProductId, maxProducts } = this.dataset;

    if (!sectionId || !searchUrl) return;

    // The current product is in the store too (scripts.liquid records it on
    // every product page), and recommending the page you're already on is
    // noise — drop it before deciding whether there's anything to show.
    const viewedProducts = RecentlyViewed.getProducts().filter((id) => id !== currentProductId);

    if (viewedProducts.length === 0) return;

    const limit = Number(maxProducts) || 4;
    const url = new URL(searchUrl, location.origin);
    url.searchParams.set('q', viewedProducts.slice(0, limit).map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    try {
      const html = await sectionRenderer.getSectionHTML(sectionId, false, url);
      if (!html?.trim().length) return;

      const fragment = new DOMParser().parseFromString(html, 'text/html');
      const results = fragment.getElementById(RESULTS_ID);

      // A viewed product can since have been unpublished or deleted, in
      // which case the search comes back with nothing to show.
      if (!results || results.children.length === 0) return;

      const container = this.querySelector('[data-recently-viewed-results]');
      const content = this.querySelector('[data-recently-viewed-content]');
      if (!container || !content) return;

      container.innerHTML = results.innerHTML;
      this.#adoptSectionStylesheet(fragment);
      content.hidden = false;
    } catch (error) {
      console.error('Recently viewed products error:', error);
    }
  }

  /**
   * The cards come from a section this page never rendered, so their
   * scoped styles aren't on the page. Carry the fetched section's
   * stylesheet over with them.
   *
   * @param {Document} fragment - The parsed Section Rendering API response
   */
  #adoptSectionStylesheet(fragment) {
    const newStylesheet = fragment.querySelector('style[data-section-stylesheet]');
    if (!newStylesheet) return;

    const existing = this.querySelector('style[data-section-stylesheet]');
    if (existing) {
      existing.textContent = newStylesheet.textContent;
    } else {
      this.prepend(newStylesheet.cloneNode(true));
    }
  }
}

if (!customElements.get('recently-viewed-products-block')) {
  customElements.define('recently-viewed-products-block', RecentlyViewedProductsBlock);
}
